import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { LevelBadge, LevelProgress } from "@/components/LevelBadge";
import ReferralsCard from "@/components/ReferralsCard";
import PlansCard from "@/components/PlansCard";
import LiveNowCard from "@/components/LiveNowCard";
import { Trophy, Zap, Target, DollarSign } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  usePageMeta({
    title: "My Dashboard · PickemSports.club",
    description: "Your level, XP, wins, wallet balance, referrals, and cashouts in one place.",
    path: "/dashboard",
    noindex: true,
  });

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/users/me/stats");
      setStats(data);
    })();
  }, []);

  if (!stats) return <Layout><div className="text-white/40 py-20 text-center font-display text-2xl tracking-widest animate-pulse">LOADING...</div></Layout>;

  const cards = [
    { label: "Contests", value: stats.total_contests || 0, Icon: Target, color: "#007AFF" },
    { label: "Wins", value: stats.total_wins || 0, Icon: Trophy, color: "#FFD700" },
    { label: "XP Earned", value: stats.xp || 0, Icon: Zap, color: "#00FF66" },
    { label: "Wallet", value: `$${(stats.wallet_balance || 0).toFixed(2)}`, Icon: DollarSign, color: "#00FF66" },
  ];

  return (
    <Layout>
      <div className="mb-8">
        <div className="text-[#00FF66] text-xs uppercase tracking-widest mb-2">Player Profile</div>
        <h1 className="font-display text-4xl sm:text-5xl md:text-6xl tracking-tight">DASHBOARD</h1>
      </div>

      {/* Level card */}
      <div className="border border-white/10 bg-[#141414] p-8 mb-6">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
          <div>
            <div className="text-white/40 text-xs uppercase tracking-widest mb-1">Current Level</div>
            <div className="flex items-center gap-3">
              <LevelBadge level={user?.level} size="lg" />
              <span className="text-white/40">·</span>
              <span className="font-display text-3xl">{stats.xp} XP</span>
            </div>
          </div>
          {stats.level_info?.next && (
            <div className="text-right">
              <div className="text-white/40 text-xs uppercase tracking-widest">Next Level</div>
              <div className="flex items-center gap-2 justify-end">
                <span className="font-display text-2xl text-white/60">
                  {stats.level_info.xp_to_next} XP
                </span>
                <span className="text-white/40 text-sm">to</span>
                <LevelBadge level={stats.level_info.next} />
              </div>
            </div>
          )}
        </div>
        <LevelProgress
          level={user?.level}
          xp={stats.xp}
          levelInfo={stats.level_info}
          testId="dashboard-level-progress"
        />
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {cards.map(({ label, value, Icon, color }) => (
          <div key={label} className="border border-white/10 bg-[#141414] p-5">
            <div className="flex items-start justify-between mb-4">
              <Icon size={18} style={{ color }} />
            </div>
            <div className="font-display text-4xl" style={{ color }}>
              {value}
            </div>
            <div className="text-white/40 text-xs uppercase tracking-widest mt-1">{label}</div>
          </div>
        ))}
      </div>

      {/* Anything in flight right now */}
      <div className="mb-6">
        <LiveNowCard />
      </div>

      {/* Referrals + AI analytics plans */}
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <ReferralsCard />
        <PlansCard />
      </div>

      {/* How XP works */}
      <div className="border border-white/10 bg-[#141414] p-6">
        <div className="text-[#00FF66] text-xs uppercase tracking-widest mb-1">How XP works</div>
        <h2 className="font-display text-2xl mb-4">EARN. LEVEL. UNLOCK.</h2>
        <div className="grid md:grid-cols-4 gap-4 text-sm">
          <div className="border-l-2 border-[#00FF66] pl-3">
            <div className="font-display text-2xl">+10 XP</div>
            <div className="text-white/50">per contest entry</div>
          </div>
          <div className="border-l-2 border-[#FFD700] pl-3">
            <div className="font-display text-2xl">+100 XP</div>
            <div className="text-white/50">contest win (1st)</div>
          </div>
          <div className="border-l-2 border-[#FF2D55] pl-3">
            <div className="font-display text-2xl">+40 XP</div>
            <div className="text-white/50">top-3 finish</div>
          </div>
          <div className="border-l-2 border-[#007AFF] pl-3">
            <div className="font-display text-2xl">+15 XP</div>
            <div className="text-white/50">top-10 finish</div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
